import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  tournamentSelection,
  singlePointCrossover,
  findBestChromosome,
} from './geneticAlgorithm';

/**
 * SFLA-GA Hybrid (SFLAGA) implementation from scratch
 * Combines SFLA memeplex structure with GA operators
 */

/**
 * Calculates similarity between two chromosomes (percentage of same genes)
 */
function calculateSimilarity(chrom1: Chromosome, chrom2: Chromosome): number {
  let sameGenes = 0;
  for (let i = 0; i < chrom1.x.length; i++) {
    if (chrom1.x[i] === chrom2.x[i]) {
      sameGenes++;
    }
  }
  return sameGenes / chrom1.x.length;
}

/**
 * Checks if a chromosome is too similar to any elite (≥90% same genes)
 */
function isTooSimilarToElites(
  chrom: Chromosome,
  elites: Chromosome[],
  threshold: number = 0.90
): boolean {
  for (const elite of elites) {
    if (calculateSimilarity(chrom, elite) >= threshold) {
      return true;
    }
  }
  return false;
}

/**
 * Improved mutation for discrete encoding: mutate 1-3 random tasks
 * Optionally chooses replacement gene from top-M candidates if available
 */
function improvedMutate(
  chromosome: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  topMCandidates: number = 5
): Chromosome {
  const mutated: Chromosome = {
    x: [...chromosome.x],
    fitness: 0,
    rwp: 0,
  };

  // Mutate 1-3 random tasks
  const mutationCount = Math.floor(Math.random() * 3) + 1; // 1, 2, or 3
  const mutatedIndices = new Set<number>();

  for (let i = 0; i < mutationCount; i++) {
    let taskIdx: number;
    do {
      taskIdx = Math.floor(Math.random() * taskCount);
    } while (mutatedIndices.has(taskIdx));
    mutatedIndices.add(taskIdx);

    // Try to use top-M candidates if available
    if (topMCandidates > 0 && topMCandidates <= serviceCount) {
      // Rank services for this task by average quality
      const serviceScores: Array<{ service: number; score: number }> = [];
      for (let s = 0; s < serviceCount; s++) {
        let score = 0;
        for (let prop = 0; prop < 9; prop++) {
          const value = matrix[taskIdx]?.[s]?.[prop] ?? 0;
          score += value;
        }
        serviceScores.push({ service: s, score: score / 9 });
      }
      // Sort by score (descending) and pick from top M
      serviceScores.sort((a, b) => b.score - a.score);
      const topM = serviceScores.slice(0, topMCandidates);
      if (topM.length > 0) {
        const selected = topM[Math.floor(Math.random() * topM.length)];
        mutated.x[taskIdx] = selected.service;
      } else {
        mutated.x[taskIdx] = Math.floor(Math.random() * serviceCount);
      }
    } else {
      // Random mutation
      mutated.x[taskIdx] = Math.floor(Math.random() * serviceCount);
    }
  }

  return mutated;
}

/**
 * Repair step: fixes constraint violations by re-sampling violating genes
 * For discrete service composition, ensures all genes are within valid range
 */
function repairChromosome(
  chromosome: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][]
): Chromosome {
  const repaired: Chromosome = {
    x: [...chromosome.x],
    fitness: 0,
    rwp: 0,
  };

  // Fix any out-of-range genes
  for (let i = 0; i < taskCount; i++) {
    if (repaired.x[i] < 0 || repaired.x[i] >= serviceCount) {
      // Re-sample from valid range
      repaired.x[i] = Math.floor(Math.random() * serviceCount);
    }
  }

  // Recalculate fitness after repair
  calculateChromosomeFitness(repaired, matrix, taskCount, serviceCount);
  return repaired;
}

/**
 * Divides population into memeplexes
 */
function createMemeplexes(
  population: Chromosome[],
  memeplexCount: number
): Chromosome[][] {
  const memeplexes: Chromosome[][] = Array(memeplexCount)
    .fill(null)
    .map(() => []);

  for (let i = 0; i < population.length; i++) {
    memeplexes[i % memeplexCount].push(population[i]);
  }

  return memeplexes;
}

/**
 * Shuffles memeplexes back into population
 */
function shuffleMemeplexes(
  memeplexes: Chromosome[][],
  population: Chromosome[]
): void {
  let idx = 0;
  for (const memeplex of memeplexes) {
    for (const frog of memeplex) {
      population[idx++] = frog;
    }
  }
}

/**
 * Local search: improves worst frog using memeplex best (SFLA-inspired)
 * Optimized step size to prevent excessive gene copying on large problems
 * Enhanced: Tries multiple step sizes and keeps the best result
 */
function localSearchWorstFrog(
  worst: Chromosome,
  best: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][]
): Chromosome {
  // Limit step size: for large problems, don't copy too many genes at once
  const maxStep = Math.max(1, Math.min(5, Math.floor(taskCount / 10)));
  const minStep = 1;
  
  // Try multiple step sizes (1-3 attempts) and keep the best result
  // This improves exploitation of local neighborhoods
  let bestResult: Chromosome | null = null;
  let bestFitness = worst.fitness;
  const attempts = Math.min(3, maxStep - minStep + 1);
  
  for (let attempt = 0; attempt < attempts; attempt++) {
    const step = attempt === 0 
      ? Math.floor(Math.random() * (maxStep - minStep + 1)) + minStep
      : Math.min(maxStep, minStep + attempt);
    
    const newFrog: Chromosome = {
      x: [...worst.x],
      fitness: 0,
      rwp: 0,
    };

    // Move towards best frog (copy some genes)
    // Use Set to avoid copying same gene multiple times
    const copiedIndices = new Set<number>();
    for (let i = 0; i < step && copiedIndices.size < taskCount; i++) {
      let taskIdx: number;
      do {
        taskIdx = Math.floor(Math.random() * taskCount);
      } while (copiedIndices.has(taskIdx));
      
      copiedIndices.add(taskIdx);
      newFrog.x[taskIdx] = best.x[taskIdx];
    }

    calculateChromosomeFitness(newFrog, matrix, taskCount, serviceCount);
    
    // Keep the best result from all attempts
    if (newFrog.fitness > bestFitness) {
      bestFitness = newFrog.fitness;
      bestResult = newFrog;
    }
  }
  
  // Return best result, or worst if no improvement found
  return bestResult || worst;
}

/**
 * Evolves a memeplex using GA operators with improved strategy
 */
function evolveMemeplex(
  memeplex: Chromosome[],
  globalBest: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  crossoverRate: number,
  mutationRate: number,
  generation: number,
  maxGenerations: number
): void {
  if (memeplex.length === 0) return;

  const memeplexSize = memeplex.length;
  // Note: crossoverRate and mutationRate are expected as percentages (0-100)
  // Clamp to ensure counts don't exceed memeplex size
  const crossoverCount = Math.min(memeplexSize, Math.floor((memeplexSize * crossoverRate) / 100));
  const mutateCount = Math.min(memeplexSize, Math.floor((memeplexSize * mutationRate) / 100));

  // Sort memeplex once at the beginning (best to worst)
  // Best frog = highest fitness (index 0), worst frog = lowest fitness (index memeplexSize - 1)
  memeplex.sort((a, b) => b.fitness - a.fitness);
  const memeplexBest = memeplex[0];
  const memeplexWorst = memeplex[memeplexSize - 1];

  // Calculate elite count (top 1-3% of memeplex to protect from overwriting/mutation)
  // Use 2% as balanced value (midpoint of 1-3% range), minimum 1
  const elitePercent = 0.02; // 2% elitism
  const eliteCount = Math.max(1, Math.floor(memeplexSize * elitePercent));

    // Local search on worst frog (SFLA-inspired improvement)
    // Replace worst ONLY if strictly higher fitness; else try global-best; else random reinit
    if (memeplexSize > 2) {
      let improved = localSearchWorstFrog(
        memeplexWorst,
        memeplexBest,
        taskCount,
        serviceCount,
        matrix
      );

      if (improved.fitness > memeplexWorst.fitness) {
        // Strictly higher fitness: accept improvement
        // Early stopping: if improvement is significant (>5%), skip global best attempt
        const improvementRatio = improved.fitness / memeplexWorst.fitness;
        const significantImprovement = improvementRatio > 1.05;
        
        if (significantImprovement) {
          // Significant improvement: accept and skip global best attempt (save computation)
          memeplex[memeplexSize - 1] = improved;
          memeplex.sort((a, b) => b.fitness - a.fitness);
        } else {
          // Try with global best for potentially better result (use original worst, not improved)
          const improvedWithGlobal = localSearchWorstFrog(
            memeplexWorst,
            globalBest,
            taskCount,
            serviceCount,
            matrix
          );
          // Use the better of the two improvements
          if (improvedWithGlobal.fitness > improved.fitness) {
            memeplex[memeplexSize - 1] = improvedWithGlobal;
          } else {
            memeplex[memeplexSize - 1] = improved;
          }
          memeplex.sort((a, b) => b.fitness - a.fitness);
        }
      } else {
        // Memeplex best didn't help: try with global best
        improved = localSearchWorstFrog(
          memeplexWorst,
          globalBest,
          taskCount,
          serviceCount,
          matrix
        );
        if (improved.fitness > memeplexWorst.fitness) {
          // Strictly higher fitness: accept improvement
          memeplex[memeplexSize - 1] = improved;
          memeplex.sort((a, b) => b.fitness - a.fitness);
        } else {
        // Both failed: use hybrid reinit strategy
        // Instead of completely random, create a hybrid: 50% from worst, 50% random
        // This maintains some structure while introducing diversity
        const hybridFrog: Chromosome = {
          x: [...memeplexWorst.x],
          fitness: 0,
          rwp: 0,
        };
        
        // Randomly replace half of the genes to introduce diversity
        const replaceCount = Math.max(1, Math.floor(taskCount / 2));
        const replacedIndices = new Set<number>();
        for (let j = 0; j < replaceCount; j++) {
          let taskIdx: number;
          do {
            taskIdx = Math.floor(Math.random() * taskCount);
          } while (replacedIndices.has(taskIdx));
          replacedIndices.add(taskIdx);
          hybridFrog.x[taskIdx] = Math.floor(Math.random() * serviceCount);
        }
        
        calculateChromosomeFitness(hybridFrog, matrix, taskCount, serviceCount);
        
        // Only replace if hybrid is strictly better than worst
        // This prevents degradation while maintaining diversity
        if (hybridFrog.fitness > memeplexWorst.fitness) {
          memeplex[memeplexSize - 1] = hybridFrog;
          memeplex.sort((a, b) => b.fitness - a.fitness);
        }
      }
    }
  }

  // Crossover operations with tournament selection (k=3 or 4)
  // Use tournament selection instead of roulette wheel (robust under penalties/low fitness)
  const tournamentSize = memeplexSize >= 4 ? 4 : 3; // k=4 if possible, else k=3
  const elites = memeplex.slice(0, eliteCount);
  const children: Chromosome[] = [];
  
  // Adaptive global best usage: increases from 50% to 70% as generations progress
  // Early: more exploration (50%), Later: more exploitation (70%)
  const globalBestUsageRate = 0.5 + (generation / maxGenerations) * 0.2; // 0.5 -> 0.7
  
  for (let i = 0; i < crossoverCount; i++) {
    let parent1: Chromosome;
    let parent2: Chromosome;

    // Use global best as one parent with adaptive rate (50% -> 70%)
    // This improves convergence towards global optimum, especially in later generations
    if (Math.random() < globalBestUsageRate && memeplex.length > 0) {
      parent1 = globalBest;
      parent2 = memeplex[tournamentSelection(memeplex, tournamentSize)];
    } else if (Math.random() < 0.3 && memeplex.length > 0) {
      // 30% chance to use memeplex best as one parent (when not using global best)
      // This improves local convergence within memeplexes
      parent1 = memeplexBest;
      parent2 = memeplex[tournamentSelection(memeplex, tournamentSize)];
    } else {
      // Standard tournament selection for both parents
      parent1 = memeplex[tournamentSelection(memeplex, tournamentSize)];
      parent2 = memeplex[tournamentSelection(memeplex, tournamentSize)];
    }

    // Use both children from crossover (don't waste child2)
    let [child1, child2] = singlePointCrossover(parent1, parent2, taskCount);
    
    // Repair step: fix constraint violations
    child1 = repairChromosome(child1, taskCount, serviceCount, matrix);
    child2 = repairChromosome(child2, taskCount, serviceCount, matrix);

    // Diversity guard: if child is too similar to elites (≥90% same genes), try extra mutation
    // Only apply if mutation improves or maintains fitness (prevents degradation)
    if (isTooSimilarToElites(child1, elites, 0.90)) {
      const mutated = improvedMutate(child1, taskCount, serviceCount, matrix, 5);
      calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);
      // Only use mutation if it's better or equal (prevents degrading good solutions)
      if (mutated.fitness >= child1.fitness) {
        child1 = mutated;
      }
    }
    
    if (isTooSimilarToElites(child2, elites, 0.90)) {
      const mutated = improvedMutate(child2, taskCount, serviceCount, matrix, 5);
      calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);
      // Only use mutation if it's better or equal (prevents degrading good solutions)
      if (mutated.fitness >= child2.fitness) {
        child2 = mutated;
      }
    }

    children.push(child1, child2);
  }

  // Replace worst individuals with best children (greedy replacement)
  // Sort children by fitness (best first)
  children.sort((a, b) => b.fitness - a.fitness);
  
  // Replace worst memeplex members with best children (greedy: only replace if better)
  // Protect elite (top E frogs) from being overwritten
  let childIdx = 0;
  let memeplexIdx = memeplexSize - 1;
  while (childIdx < children.length && memeplexIdx >= eliteCount) {
    // Only replace if child is better AND we're not in elite region
    if (children[childIdx].fitness > memeplex[memeplexIdx].fitness) {
      memeplex[memeplexIdx] = children[childIdx];
      memeplexIdx--;
    }
    childIdx++;
  }

  // Re-sort memeplex after replacements
  memeplex.sort((a, b) => b.fitness - a.fitness);

  // Mutation operations with adaptive strategy
  // Early generations: more exploration, later: more exploitation
  // Improved: Slower decay and maintain minimum exploration (0.3) to prevent premature convergence
  // This prevents the algorithm from becoming too exploitative too quickly with more iterations
  const explorationRate = Math.max(0.3, 1.0 - (generation / maxGenerations) * 0.4); // 1.0 -> 0.6 (slower decay, higher minimum)

  // Skip mutation if all frogs are elite (edge case)
  if (eliteCount >= memeplexSize) {
    return;
  }

  for (let i = 0; i < mutateCount; i++) {
    // Prefer mutating worse individuals (but not always)
    // Protect elite (top E frogs) from mutation
    let idx: number;
    if (Math.random() < 0.7) {
      // 70% chance to mutate worst half (excluding elite)
      const worstHalfStart = Math.max(eliteCount, Math.floor(memeplexSize / 2));
      idx = worstHalfStart + Math.floor(Math.random() * (memeplexSize - worstHalfStart));
    } else {
      // Random selection from non-elite region
      idx = eliteCount + Math.floor(Math.random() * (memeplexSize - eliteCount));
    }

    // Use improved mutation: mutate 1-3 random tasks with top-M candidate selection
    let mutated = improvedMutate(memeplex[idx], taskCount, serviceCount, matrix, 5);
    
    // Repair step: fix constraint violations
    mutated = repairChromosome(mutated, taskCount, serviceCount, matrix);

    // Accept mutation if better, or with probability based on exploration rate
    // Stricter acceptance threshold: 0.98 -> 0.95 (only accept if very close to current fitness)
    // This prevents accepting solutions that are significantly worse
    const acceptanceThreshold = Math.max(0.95, 0.98 - (generation / maxGenerations) * 0.03); // 0.98 -> 0.95
    if (mutated.fitness > memeplex[idx].fitness || 
        (Math.random() < explorationRate && mutated.fitness >= memeplex[idx].fitness * acceptanceThreshold)) {
      memeplex[idx] = mutated;
    }
  }

  // Final sort to maintain order
  memeplex.sort((a, b) => b.fitness - a.fitness);
}

/**
 * Runs SFLAGA for specified generations
 */
export async function runSFLAGA(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    memeplexCount: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void
): Promise<Chromosome> {
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount
  );

  // Evaluate initial population
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Sort population by fitness (descending) before creating memeplexes
  // SFLA requires global sorting before memeplex formation to ensure proper ranked distribution
  // Best frog = highest fitness, worst frog = lowest fitness
  population.sort((a, b) => b.fitness - a.fitness);

  // Create memeplexes
  let memeplexes = createMemeplexes(population, parameters.memeplexCount);

  // Adaptive rates tracking
  let bestFitnessHistory: number[] = [];
  const stagnationThreshold = 15; // 10-20 generations (using 15 as midpoint)
  let currentMutationRate = parameters.mutationRate;
  let currentImmigrantPercent = 0.03; // Start with 3% immigrants (midpoint of 2-5%)

  // Evolution loop
  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100; // Update UI every 100ms (PERFORMANCE OPTIMIZATION)
  
  for (let gen = 0; gen < parameters.generations; gen++) {
    // Find global best
    const globalBest = findBestChromosome(population);
    const currentBestFitness = globalBest.fitness;
    
    // Track best fitness history for adaptive rates
    bestFitnessHistory.push(currentBestFitness);
    if (bestFitnessHistory.length > stagnationThreshold) {
      bestFitnessHistory.shift(); // Keep only last N generations
    }

    // Apply adaptive rates: adjust mutation and immigrants based on stagnation
    if (bestFitnessHistory.length >= stagnationThreshold) {
      const recentBest = bestFitnessHistory[bestFitnessHistory.length - 1];
      const olderBest = bestFitnessHistory[0];
      
      // Check if fitness is stagnating (not improving by at least 1%)
      // More lenient threshold to avoid disrupting good convergence
      if (recentBest <= olderBest * 1.01) { // 1% improvement threshold (was 0.1%)
        // Stagnation detected: increase mutation by ~15% (was 30%), add +1% immigrants (was +2%)
        // Don't reshuffle memeplexes - let them continue converging locally
        currentMutationRate = Math.min(100, currentMutationRate * 1.15);
        currentImmigrantPercent = Math.min(0.05, currentImmigrantPercent + 0.01);
        
        // Don't reshuffle - this breaks local convergence that might be working
        // shuffleMemeplexes(memeplexes, population);
        // population.sort((a, b) => b.fitness - a.fitness);
        // memeplexes = createMemeplexes(population, parameters.memeplexCount);
      } else {
        // Improving: slowly reduce mutation (but keep minimum)
        currentMutationRate = Math.max(parameters.mutationRate * 0.5, currentMutationRate * 0.98);
        currentImmigrantPercent = Math.max(0.02, currentImmigrantPercent * 0.99);
      }
    }

    // Evolve each memeplex using GA operators with adaptive mutation rate
    for (const memeplex of memeplexes) {
      evolveMemeplex(
        memeplex,
        globalBest,
        taskCount,
        serviceCount,
        matrix,
        parameters.crossoverRate,
        currentMutationRate,
        gen,
        parameters.generations
      );
    }

    // Shuffle memeplexes (redistribute to maintain diversity)
    shuffleMemeplexes(memeplexes, population);

    // Sort population by fitness (descending) before recreating memeplexes
    // This ensures proper cross-mixing and ranked redistribution (critical for SFLA shuffling)
    population.sort((a, b) => b.fitness - a.fitness);

    // Elitism: keep top 1-3% unchanged each generation
    const elitePercent = 0.02; // 2% elitism (midpoint of 1-3%)
    const eliteCount = Math.max(1, Math.floor(population.length * elitePercent));
    const elites = population.slice(0, eliteCount).map(chrom => ({
      x: [...chrom.x],
      fitness: chrom.fitness,
      rwp: chrom.rwp,
    }));

    // Smart immigrants: replace bottom 2-5% with guided immigrants (global best with mutations)
    // This provides better diversity injection than pure random
    const immigrantCount = Math.max(1, Math.floor(population.length * currentImmigrantPercent));
    for (let i = 0; i < immigrantCount; i++) {
      const replaceIdx = population.length - 1 - i;
      // Only replace if not in elite region
      if (replaceIdx >= eliteCount) {
        // Create guided immigrant: start from global best, mutate 10-20% of genes
        const immigrant: Chromosome = {
          x: [...globalBest.x],
          fitness: 0,
          rwp: 0,
        };
        
        // Mutate 10-20% of genes to introduce diversity while maintaining quality
        const mutateCount = Math.max(1, Math.floor(taskCount * (0.1 + Math.random() * 0.1)));
        const mutatedIndices = new Set<number>();
        for (let j = 0; j < mutateCount; j++) {
          let taskIdx: number;
          do {
            taskIdx = Math.floor(Math.random() * taskCount);
          } while (mutatedIndices.has(taskIdx));
          mutatedIndices.add(taskIdx);
          immigrant.x[taskIdx] = Math.floor(Math.random() * serviceCount);
        }
        
        // Repair to ensure feasibility
        const repairedChrom = repairChromosome(immigrant, taskCount, serviceCount, matrix);
        population[replaceIdx] = repairedChrom;
      }
    }

    // Re-sort after immigrants injection
    population.sort((a, b) => b.fitness - a.fitness);

    // Restore elites only if they're strictly better (don't overwrite naturally evolved better solutions)
    // This prevents restoring elites that are no longer the best after natural evolution
    for (let i = 0; i < eliteCount && i < elites.length; i++) {
      if (elites[i].fitness > population[i].fitness) {
        population[i] = elites[i];
      }
    }

    // Final sort to maintain order
    population.sort((a, b) => b.fitness - a.fitness);

    // Recreate memeplexes for next generation
    memeplexes = createMemeplexes(population, parameters.memeplexCount);

    // Batch UI updates - only update every 100ms or every 10 generations (PERFORMANCE OPTIMIZATION)
    const now = Date.now();
    const shouldUpdate = (now - lastUpdateTime > UPDATE_INTERVAL) || (gen % 10 === 9) || (gen === parameters.generations - 1);
    
    if (shouldUpdate) {
      const best = findBestChromosome(population);
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, best.fitness);
      }
      lastUpdateTime = now;
      // Yield control to allow UI updates
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  return findBestChromosome(population);
}

